// Phone number normalization + validation.
// Mirrors the backend `User.normalize_phone` / `User.to_e164_jp` so the FE and BE
// agree on what a stored phone number looks like. Root cause of the original bug:
// a stray leading space in a stored number broke the SMS "leading 0 → +81" step,
// so the 2FA code was never delivered.
import { isValidPhoneNumber, AsYouType } from 'libphonenumber-js/max';

// Full-width digits (０-９) → half-width (0-9).
function toHalfWidthDigits(s: string): string {
    return s.replace(/[０-９]/g, function (c) {
        return String.fromCharCode(c.charCodeAt(0) - 0xFEE0)
    })
}

// Storage form: full-width digits → half-width, remove ALL whitespace
// (including the full-width space U+3000). Hyphens are kept for readability.
export function normalizePhone(str: string): string {
    if (!str) return str
    return toHalfWidthDigits(str)
        .replace(/[‐‑‒–—―−₋－]/g, "-") // unicode dashes → ASCII hyphen
        .replace(/＋/g, "+")            // full-width plus → ASCII plus
        .replace(/[\s　]/g, "")          // remove all whitespace incl full-width space
}

// Digits-only form (full-width→half, then drop every non-digit incl. "+"). Used to
// decide whether a phone was really CHANGED: reformatting / hyphen / space differences
// yield the same digits, so they don't count as a change.
export function phoneDigits(str: string): string {
    if (!str) return ""
    return normalizePhone(str).replace(/[^0-9]/g, "")
}

// True if `str` is a valid Japanese phone number. Accepts both domestic ("090…")
// and international ("+8190…") forms; empty is allowed (phone is optional).
// Backed by libphonenumber-js (same Google metadata as the backend's `phonelib`
// gem) so FE and BE validation agree. `/max` metadata is used for accurate
// national-number validation (rejects bogus prefixes, not just wrong length).
export function isValidJpPhone(str: string): boolean {
    if (!str) return true
    const v = normalizePhone(str)
    if (v == "") return true
    return isValidPhoneNumber(v, "JP")
}

// Format-as-you-type for a JP phone. Domestic → hyphen groups (090-1234-5678,
// 03-1234-5678 per area code); +81… → international grouping. Only ADDS separators
// (never drops digits). Feed the raw input on each keystroke; normalize first so
// full-width digits become half-width. Used on the staff phone input (onChange).
export function formatJpPhoneAsYouType(str: string): string {
    if (!str) return str
    return new AsYouType("JP").input(normalizePhone(str))
}
